/**
 * PhotorealisticTab — Phase 5 (Feb 2026 PROMPT_CLOSE_ALL_REMAINING)
 *
 * Sidebar tab that hosts the Unreal Engine 5 Pixel Streaming viewer.
 * Shown for ALL infra sub-types once the BIM pipeline has produced a
 * usable 3D model (glb_url or fbx_url present in bim_pipeline state).
 */
import React, { useState, useEffect } from "react";
import axios from "axios";
import PixelStreamViewer from "./PixelStreamViewer";

const API = process.env.REACT_APP_BACKEND_URL;

export default function PhotorealisticTab({ projectId, subType }) {
  const [hasModel, setHasModel] = useState(false);
  const [glbUrl, setGlbUrl] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancel = false;
    async function check() {
      try {
        const { data } = await axios.get(
          `${API}/api/projects/${projectId}/bim/status`,
          { withCredentials: true });
        if (cancel) return;
        const blender = data?.blender || {};
        setGlbUrl(blender.glb_url || "");
        setHasModel(Boolean(blender.glb_url || blender.fbx_url));
      } catch (e) {
        if (!cancel) setHasModel(false);
      } finally {
        if (!cancel) setLoading(false);
      }
    }
    check();
    return () => { cancel = true; };
  }, [projectId]);

  return (
    <div data-testid="photorealistic-tab"
         className="flex flex-col gap-3 p-4">
      <div className="flex items-center gap-3">
        <h2 className="text-lg font-bold text-zinc-100"
            data-testid="photorealistic-tab-title">
          Photorealistic Preview
        </h2>
        <span className="text-xs px-2 py-0.5 rounded
                         bg-violet-500/20 text-violet-300 font-mono">
          Unreal Engine 5
        </span>
        {subType && (
          <span className="text-xs text-zinc-500">{subType}</span>
        )}
      </div>
      <p className="text-xs text-zinc-400 max-w-2xl">
        Live Unreal Engine 5 rendering of this {subType || "infrastructure"}{" "}
        project, streamed at 1080p over WebRTC. Photoreal materials,
        ray-traced shadows, dynamic time-of-day, and cinematic camera
        paths. Billed per second of GPU compute — click <em>Stop</em>{" "}
        when finished.
      </p>
      {loading ? (
        <div className="text-zinc-500 text-sm">Loading…</div>
      ) : (
        <PixelStreamViewer projectId={projectId} hasModel={hasModel} />
      )}
      {hasModel && glbUrl && (
        <div className="text-[10px] text-zinc-500 font-mono">
          Source GLB: {glbUrl.substring(0, 80)}{glbUrl.length > 80 ? "…" : ""}
        </div>
      )}
    </div>
  );
}
